import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const ALL_STORES_OPTION = { id: "all", name: "Both Stores" };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStoreState] = useState(null);

  const fetchStores = useCallback(async () => {
    try {
      const res = await api.get("/stores");
      const list = res.data;
      setStores(list);
      const savedId = localStorage.getItem("gm_store");
      if (savedId === "all") {
        setSelectedStoreState(ALL_STORES_OPTION);
      } else {
        const saved = list.find((s) => String(s.id) === savedId);
        setSelectedStoreState(saved || list[0] || null);
      }
    } catch {
      /* not authenticated yet — expected */
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      await fetchStores();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchStores]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const setSelectedStore = useCallback((store) => {
    setSelectedStoreState(store);
    if (store?.id) localStorage.setItem("gm_store", String(store.id));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    await fetchStores();
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    setStores([]);
    setSelectedStoreState(null);
    localStorage.removeItem("gm_store");
  };

  // Build the store options: employees see only their stores, others get "Both Stores"
  const storeOptions =
    user && ["owner", "store_manager", "accountant"].includes(user.role) && stores.length > 1
      ? [ALL_STORES_OPTION, ...stores]
      : stores;

  // Resolve the actual store_id to send to API (null for "all")
  const activeStoreId = selectedStore?.id === "all" ? null : selectedStore?.id ?? null;

  return (
    <AuthContext.Provider
      value={{
        user, loading, stores, storeOptions, selectedStore,
        setSelectedStore, activeStoreId, login, logout, checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
